import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Button, Card, Field } from '../../components/kit';
import { ContractModal } from '../../components/ContractModal';
import { paymentsApi, type BankOption } from '../../api/payments';
import { bankLogo } from '../../components/bankLogos';
import { ApiRequestError } from '../../api/client';
import '../page.css';
import './payment.css';

export function NewPayment() {
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [bankKey, setBankKey] = useState('');
  const [amount, setAmount] = useState('');
  const [installment, setInstallment] = useState(1);
  const [card, setCard] = useState({ number: '', expiryMonth: '', expiryYear: '', cvv: '', holder: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showContract, setShowContract] = useState<string | null>(null);

  useEffect(() => {
    paymentsApi.banks().then((b) => {
      setBanks(b);
      if (b[0]) setBankKey(b[0].key);
    }).catch(() => setError('Bankalar yüklenemedi'));
  }, []);

  // Seçili bankanın desteğine göre en fazla 6 taksit (şimdilik vade farksız / %0).
  const selectedBank = banks.find((b) => b.key === bankKey);
  const maxInst = selectedBank?.installments?.enabled ? Math.min(selectedBank.installments.max ?? 1, 6) : 1;

  // Banka değişince seçili taksit üst sınırı aşıyorsa Tek Çekim'e dön.
  useEffect(() => { if (installment > maxInst) setInstallment(1); }, [maxInst, installment]);

  const setCardField = (k: keyof typeof card) => (e: ChangeEvent<HTMLInputElement>) =>
    setCard((c) => ({ ...c, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await paymentsApi.initiate({
        bankKey,
        amount: Number(amount),
        installment,
        termsAccepted: accepted,
        card: {
          number: card.number.replace(/\s+/g, ''),
          expiryMonth: card.expiryMonth,
          expiryYear: card.expiryYear,
          cvv: card.cvv,
          holder: card.holder || undefined,
        },
      });
      // QNB (IP-uyumlu): banka 3D form HTML'ini sunucu döner → üst pencerede aç.
      if (res.relayHtml) {
        document.open();
        document.write(res.relayHtml);
        document.close();
        return;
      }
      // EST/Garanti: tarayıcı formu doğrudan bankanın gateway'ine POST eder (kart bankaya gider).
      if (res.gatewayUrl && res.fields) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = res.gatewayUrl;
        form.style.display = 'none';
        for (const [name, value] of Object.entries(res.fields)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
        return;
      }
      setError('Banka yanıtı beklenmedik biçimde geldi');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Ödeme başlatılamadı');
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-eyebrow">Tahsilat</div>
        <h1>Ödeme Yap</h1>
        <p className="page-lead">Banka ve taksit seçin, kart bilgilerinizi girin. Ödeme 3D Secure ile güvenli tamamlanır.</p>
      </div>

      <form className="pay-grid" onSubmit={onSubmit}>
        <Card title="Ödeme bilgileri">
          {error && <div className="auth-error" style={{ marginBottom: 14 }}>{error}</div>}
          <div className="field" style={{ marginBottom: 14 }}>
            <label>Banka</label>
            <div className="bank-picker">
              {banks.map((b) => {
                const logo = bankLogo(b.key);
                const sel = b.key === bankKey;
                return (
                  <button type="button" key={b.key} aria-pressed={sel}
                    className={`bank-tile${sel ? ' sel' : ''}`} onClick={() => setBankKey(b.key)}>
                    <span className="bank-tile-logo">
                      {logo ? <img src={logo} alt={b.label} /> : <strong>{b.label}</strong>}
                    </span>
                    <span className="bank-tile-label">{b.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="pay-row">
            <Field label="Tutar (₺)" id="amount" type="number" step="0.01" min="0" value={amount}
              onChange={(e) => setAmount(e.target.value)} required />
            <div className="field">
              <label htmlFor="inst">Taksit</label>
              <select id="inst" className="input" value={installment}
                onChange={(e) => setInstallment(Number(e.target.value))}>
                {Array.from({ length: maxInst }, (_, i) => i + 1).map((n) => {
                  const amt = Number(amount);
                  const monthly = n > 1 && amt > 0
                    ? ` — aylık ₺${(amt / n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '';
                  return <option key={n} value={n}>{n === 1 ? 'Tek Çekim' : `${n} Taksit${monthly}`}</option>;
                })}
              </select>
            </div>
          </div>
        </Card>

        <Card title="Kart bilgileri">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Kart Numarası" id="cardno" inputMode="numeric" autoComplete="cc-number"
              placeholder="0000 0000 0000 0000" value={card.number} onChange={setCardField('number')} required />
            <Field label="Kart Üzerindeki İsim" id="holder" autoComplete="cc-name"
              value={card.holder} onChange={setCardField('holder')} />
            <div className="pay-row3">
              <Field label="Ay (AA)" id="mm" inputMode="numeric" placeholder="12" maxLength={2}
                value={card.expiryMonth} onChange={setCardField('expiryMonth')} required />
              <Field label="Yıl (YY)" id="yy" inputMode="numeric" placeholder="30" maxLength={2}
                value={card.expiryYear} onChange={setCardField('expiryYear')} required />
              <Field label="CVV" id="cvv" inputMode="numeric" placeholder="123" maxLength={4}
                value={card.cvv} onChange={setCardField('cvv')} required />
            </div>
            <label className="terms-check">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span>
                <button type="button" className="linklike" onClick={() => setShowContract('hizmet')}>
                  Hizmet Sözleşmesi
                </button>
                ’ni okudum ve kabul ediyorum.
              </span>
            </label>
            <Button type="submit" disabled={busy || !bankKey || !accepted}>
              {busy ? 'Bankaya yönlendiriliyor…' : 'Güvenli Öde'}
            </Button>
            <p className="pay-note">🔒 Kart bilgileriniz saklanmaz; yalnızca banka doğrulaması için iletilir.</p>
          </div>
        </Card>
      </form>

      <ContractModal contractKey={showContract} onClose={() => setShowContract(null)} />
    </div>
  );
}
